/**
 * ONE ROUND WHATSAPP BUTTON, WITH A BUBBLE THAT INTRODUCES IT AND THEN GOES AWAY.
 *
 * It shipped as a dark pill carrying the words "Need help?" — DK's own styling,
 * `--shop-contrast`, which is near-black. The owner, seeing it on a business
 * page (2026-10-03): "why the floating is black?! And make it one button, with
 * bubble that shows need help? we're here for you. And then disappear. show it
 * for several seconds only. and make it only whatsapp button nice."
 *
 * So: the button is a circle in WhatsApp's own green, the colour a shopper
 * already reads as "this opens WhatsApp", and the words are a bubble beside it
 * that fades out after a few seconds, leaving the corner to the button alone.
 *
 * The bubble is decoration: it is `aria-hidden`, because the link already
 * carries the whole sentence as its accessible name, and a screen reader should
 * not hear the greeting twice — nor lose it when the bubble goes. A reader who
 * prefers reduced motion gets the same bubble without the fade.
 */
import React, { useEffect, useState } from 'react';
import { MaskGlyph } from '../../Pages/Shop/ShopActionButtons';
import { inAppBrowserNow } from '../../utils/whatsappLink';
import WhatsAppLink from '../WhatsAppLink/WhatsAppLink';
import whatsappIcon from '../../assets/whatsapp.png';
import styles from './shopFloatingDock.module.css';

/** How long the greeting stays before it leaves the shopper alone. */
export const BUBBLE_MS = 6000;

export default function FloatingWhatsappButton({ whatsappUrl, shopName, productName, t }) {
  const message = productName
    ? t('shop.needHelpProductMessage', { name: shopName, product: productName, defaultValue: 'Hi, I came from the {{name}} shop about {{product}}' })
    : t('shop.needHelpMessage', { name: shopName, defaultValue: 'Hi, I came from the {{name}} shop' });

  // Shown on arrival, gone a few seconds later — and never shown again on this
  // page, so it greets rather than nags.
  const [greeting, setGreeting] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setGreeting(false), BUBBLE_MS);
    return () => clearTimeout(id);
  }, []);

  const greetingText = t('shop.needHelpGreeting', { defaultValue: 'Need help? We’re here for you' });
  /* THE NAME TELLS THE TRUTH (2026-10-14). Inside Facebook's and Instagram's browser this link opens no new tab
     — wa.me is a universal / app link and a phone honours one only for a same-frame tap (utils/whatsappLink.js)
     — so the screen reader is not promised one. Everywhere else the wording is unchanged. */
  const where = inAppBrowserNow()
    ? t('shop.needHelpWhereSameTab', { name: shopName, defaultValue: 'WhatsApp chat with {{name}}' })
    : t('shop.needHelpWhere', { name: shopName, defaultValue: 'WhatsApp chat with {{name}} (opens in a new tab)' });

  return (
    <div className={styles.helpWrap}>
      {greeting && (
        <span className={styles.bubble} aria-hidden="true" data-testid="shop-floating-bubble">
          {greetingText}
        </span>
      )}
      <WhatsAppLink
        href={`${whatsappUrl}?text=${encodeURIComponent(message)}`}
        className={styles.help}
        testId="shop-floating-whatsapp"
        ariaLabel={`${greetingText} — ${where}`}
      >
        <MaskGlyph src={whatsappIcon} className={styles.glyph} />
      </WhatsAppLink>
    </div>
  );
}
